import { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useCountries } from '@/features/country/hooks';

/**
 * Reusable MULTI-select country picker for every agent dispatch form
 * (predicate, strategy, protocol, report, dossier).
 *
 * Source of truth is the operator-configured ``COUNTRIES_ENABLED`` env
 * var, surfaced via ``GET /api/countries`` and cached by ``useCountries()``.
 * Order in the dropdown matches the order in the env string.
 *
 * The picker is a Popover containing a checkbox-style CommandList +
 * search input. Selected codes also render as removable chips next to
 * the trigger so the current selection is visible without opening the
 * menu.
 *
 * Behavior:
 *   - When ``value`` is empty and the catalog has loaded, this component
 *     fires ``onChange`` with the first available code (so the form has
 *     at least one country to dispatch with by default).
 *   - If the catalog is empty (operator misconfigured), the trigger
 *     renders ``"No countries enabled"`` and the underlying control is
 *     disabled — the form can't submit.
 */
/** Apply the picker's allow/block filters. ``only`` (allow-list) wins over
 *  ``exclude`` (block-list); both case-insensitive on ISO-2 code. Pure +
 *  exported for testing. */
export function selectableCountries<T extends { code: string }>(
  all: readonly T[],
  opts: { only?: readonly string[]; exclude?: readonly string[] },
): T[] {
  const onlySet = new Set((opts.only ?? []).map((c) => c.toUpperCase()));
  const excludeSet = new Set((opts.exclude ?? []).map((c) => c.toUpperCase()));
  return all.filter((c) => {
    const code = c.code.toUpperCase();
    return onlySet.size > 0 ? onlySet.has(code) : !excludeSet.has(code);
  });
}

export function CountrySelectField({
  value,
  onChange,
  id = 'country',
  label = 'Country',
  description = "Pick the country this report targets. Scopes the agent's regulatory lookups and citation gates.",
  exclude,
  only,
  hideLabel = false,
  labelOverrides,
  productType,
}: {
  /** Currently selected country codes. */
  value: string[];
  /** Replaces the full selection. */
  onChange: (codes: string[]) => void;
  id?: string;
  label?: string;
  description?: string;
  /** Suppress the built-in label when a wrapping element (e.g. a numbered
   *  step header) already provides one for ``id``. Avoids a duplicate label. */
  hideLabel?: boolean;
  /** Uppercase ISO-2 codes to filter out of the picker. Use when an
   *  agent doesn't apply to a country that's otherwise enabled (e.g.
   *  predicate excludes EU because MDR has no 510(k)-style predicate
   *  concept — equivalence lives inside the CER instead). */
  exclude?: readonly string[];
  /** Uppercase ISO-2 codes to RESTRICT the picker to (allow-list). When set,
   *  ONLY these are selectable regardless of what's enabled — for agents that
   *  are inherently single-jurisdiction (e.g. the predicate finder is US-only,
   *  a 510(k) concept). Takes precedence over ``exclude``. */
  only?: readonly string[];
  /** Per-code display-label overrides (uppercase ISO-2 → label); unset
   *  codes keep the catalog label. */
  labelOverrides?: Readonly<Record<string, string>>;
  /** Scope the catalog to this product type's admin-enabled countries
   *  ('device' | 'drug' | 'biologic' | 'combination'). Omitted = the union
   *  across all types. */
  productType?: string | null;
}) {
  const { data: allCountries = [], isLoading } = useCountries(productType);
  const countries = selectableCountries(allCountries, { only, exclude }).map(
    (c) => ({
      ...c,
      label: labelOverrides?.[c.code.toUpperCase()] ?? c.label,
    }),
  );
  const [open, setOpen] = useState(false);

  // No auto-default — the user must deliberately pick at least one country.
  // Forms should gate their submit button on ``country.length > 0``.

  const disabled = isLoading || countries.length === 0;
  const selectedSet = new Set(value);

  const toggle = (code: string) => {
    if (selectedSet.has(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const remove = (code: string) => {
    onChange(value.filter((c) => c !== code));
  };

  const selectedLabels = value
    .map((code) => countries.find((c) => c.code === code)?.label ?? code)
    .join(', ');

  const triggerText = isLoading
    ? 'Loading…'
    : countries.length === 0
      ? 'No countries enabled'
      : value.length === 0
        ? 'Pick countries…'
        : value.length === 1
          ? selectedLabels
          : `${value.length} countries`;

  return (
    <div className="space-y-2">
      {!hideLabel && (
        <Label htmlFor={id} className="text-[13px] font-semibold text-navy">
          {label}
        </Label>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              id={id}
              type="button"
              disabled={disabled}
              className={cn(
                'inline-flex items-center gap-2 text-sm py-2 px-3 rounded-md border transition-colors min-w-[220px]',
                value.length > 0
                  ? 'bg-white text-navy border-line-strong hover:border-navy'
                  : 'bg-white text-muted border-line-strong hover:border-navy',
                disabled && 'opacity-50 cursor-not-allowed',
              )}
            >
              <span className="flex-1 text-left truncate">{triggerText}</span>
              <ChevronDown
                className={cn(
                  'size-4 transition-transform shrink-0',
                  open && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="p-0 w-[280px]">
            <Command>
              <CommandInput placeholder="Search countries…" className="h-9" />
              <CommandList>
                <CommandEmpty>No countries match.</CommandEmpty>
                <CommandGroup>
                  {countries.map((c) => {
                    const isSelected = selectedSet.has(c.code);
                    return (
                      <CommandItem
                        key={c.code}
                        value={c.label}
                        onSelect={() => toggle(c.code)}
                        className="flex items-center gap-2.5 cursor-pointer"
                      >
                        <span
                          className={cn(
                            'inline-flex items-center justify-center size-4 rounded border',
                            isSelected
                              ? 'bg-navy border-navy text-white'
                              : 'bg-white border-line-strong',
                          )}
                          aria-hidden="true"
                        >
                          {isSelected && <Check className="size-3" />}
                        </span>
                        <span className="flex-1">{c.label}</span>
                        <span className="text-[10px] text-muted uppercase tracking-wider">
                          {c.code}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
              {value.length > 0 && (
                <div className="border-t border-line p-2">
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="w-full text-xs font-semibold text-muted hover:text-navy py-1"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </Command>
          </PopoverContent>
        </Popover>

        {value.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => remove(code)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold py-1 pl-2 pr-1.5 rounded-md border border-line bg-white text-navy hover:border-navy transition-colors group"
            title={`Remove ${code}`}
          >
            <span>{code}</span>
            <X
              className="size-3 text-muted group-hover:text-navy"
              aria-label={`Remove ${code}`}
            />
          </button>
        ))}
      </div>
      <p className="text-[12.5px] text-muted m-0">{description}</p>
    </div>
  );
}
